import { useRoute } from 'vue-router'
import { nextTick, reactive, ref, watch } from 'vue'
import usePost from '../../../core/composables/api/usePost'
import { ApiRequestStatus } from '../../../core/models/api/api-request-status'
import { useSnackStore } from '../../../stores/snack.store'
import { useEventBus } from '@vueuse/core'
import { EventKey } from '../../../core/events/event-key'

export const usePrepareSuperpowerSlideAnalysis = (
  feedbackTopicId: string,
  initialSlideProperties: {
    updatedAt: string | null
    spontaneousFeedbackIds: Array<string>
  },
) => {
  const route = useRoute()
  const dialogId = route.params.id as string
  const snackStore = useSnackStore()
  const eventBus = useEventBus(EventKey.dialogSlideUpdated)
  const hasChanged = ref(false)
  const isInitializing = ref(true)

  const { mutate, status: updateStatus } = usePost<
    string,
    {
      spontaneousFeedbackIds: string[]
    }
  >(`dialog/prepare/${dialogId}/superpower-slide/${feedbackTopicId}`)

  const inputData = reactive<{ slideId: string; spontaneousFeedbackIds: Array<string> }>({
    slideId: feedbackTopicId,
    spontaneousFeedbackIds: [],
  })

  const applyFormData = (source: { spontaneousFeedbackIds?: ReadonlyArray<string> }) => {
    if (source.spontaneousFeedbackIds)
      inputData.spontaneousFeedbackIds = [...source.spontaneousFeedbackIds]
  }

  const hydrateForm = () => {
    isInitializing.value = true

    if (initialSlideProperties.updatedAt) {
      applyFormData({
        spontaneousFeedbackIds: [...initialSlideProperties.spontaneousFeedbackIds],
      })
    }

    nextTick(() => {
      isInitializing.value = false
      hasChanged.value = false
    })
  }

  const updateSlide = async () => {
    await mutate(inputData)

    if (updateStatus.value === ApiRequestStatus.Success) {
      snackStore.showInfo('Daten gespeichert.')
      eventBus.emit({ dialogId, slideId: feedbackTopicId })
      hasChanged.value = false
    } else if (updateStatus.value === ApiRequestStatus.Error) {
      snackStore.showError('Daten nicht gespeichert.')
    }
  }

  hydrateForm()

  watch(
    inputData,
    () => {
      if (isInitializing.value) return

      hasChanged.value = true
    },
    { deep: true },
  )

  return { updateSlide, hasChanged, inputData }
}
